'use no memo';
import { Transaction, TransactionAccountList, User } from '@kalos/kalos-rpc';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  Button,
  Checkbox,
  cn,
  DataTable,
  DataTableColumnHeader,
  DataTablePagination,
  Dialog,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form as FormContext,
  LoadingIcon,
  Skeleton,
  toast,
  TooltipProvider,
} from '@kalos/ui';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { default as IconButton } from '@mui/material/IconButton';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { format, isValid, parseISO } from 'date-fns';
import { isEqual } from 'lodash';
import { PageSizes, PDFDocument, type PDFImage } from 'pdf-lib';
import { type FC, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { JobPreview } from '../../../components/JobPreview';
import {
  FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME,
  NULL_TIME,
  PERMISSION_NAME_RECEIPT_REVIEW,
} from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import { queryKeys } from '../../../hooks/react-query/constants';
import { useTimesheetDepartmentListQuery } from '../../../hooks/react-query/useTimesheetDepartmentQuery';
import { useTransactionAccountBatchGet } from '../../../hooks/react-query/useTransactionAccountsQuery';
import {
  type TransactionQueryFilters,
  useTransactionBatchGetQuery,
} from '../../../hooks/react-query/useTransactionQuery';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import { debounce, prettyMoney, timestamp } from '../../../tools/helpers';
import { RowTransactionLineItemContextWrapper } from '../../Transactions/components/TransactionAdmin';
import { Alert } from '../Alert';
import { CompareTransactions } from '../CompareTransactions';
import { Confirm } from '../Confirm';
import { ErrorBoundary } from '../ErrorBoundary';
import { Form } from '../Form';
import { Modal } from '../Modal';
import { type Schema } from '../PlainForm';
import { Tooltip } from '../Tooltip';
import { UploadPhotoOCR } from '../UploadPhotoOCR';
import { UploadPhotoTransaction } from '../UploadPhotoTransaction';
import { QuickBooksBillSyncDialog } from './components/QuickBooksBillSyncDialog';
import { TransactionActions } from './components/TransactionActions';
import { TransactionCreateAccordion } from './components/TransactionCreateAccordion';
import {
  type ExportType,
  TransactionExportComponents,
  TransactionExportDialog,
} from './components/TransactionExport';
import TransactionRejectReason from './components/TransactionRejectReason';
import { TransactionSearchForm } from './components/transactionSearchForm/TransactionSearchForm';
import {
  type TransactionSearchSchemeType,
  useTransactionSearchForm,
} from './components/transactionSearchForm/utils';
import { ACTIONS, type MergeDocuments, reducer, type State } from './reducer';
import { formatTransactionForExport, formatTransactionForQuickBooks } from './utils/formatters';

// const removeDuplicates = (value: Transaction, index: number, self: Transaction[]) => {
//   return self.findIndex((t) => t.id === value.id) === index;
// };
export type Props = { stickyHeaderSlot?: React.ReactNode } & (
  | {
      isSelector: true;
      setTransactions: React.Dispatch<React.SetStateAction<Transaction[]>>;
      selectionLimit: number;
      defaultStatusId?: number;
      defaultJobId?: number;
      disableFilterStatusSelector?: boolean;
      selectorTitle?: string;
      disabledParamSync?: boolean;
      disabledSelectionIds?: number[];
    }
  | {
      isSelector?: undefined;
      setTransactions?: undefined;
      selectionLimit?: undefined;
      selectorTitle?: undefined;
      disabledParamSync?: undefined;
      disabledSelectionIds?: undefined;
    }
);
const TruncatedCell: FC<{ text?: string; className?: string }> = ({ text, className }) => {
  if (!text) return null;
  return (
    <Tooltip content={text} placement="top">
      <div className={cn('truncate', className)}>{text}</div>
    </Tooltip>
  );
};

const ExpandableCell: FC<{ text?: string; className?: string }> = ({ text, className }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const toggle = () => setExpanded((value) => !value);
  return (
    <div
      role="button"
      tabIndex={0}
      title={expanded ? 'Click to collapse' : 'Click to expand'}
      className={cn(
        'cursor-pointer',
        expanded ? 'whitespace-pre-wrap break-words' : 'truncate',
        className,
      )}
      onClick={(e) => {
        e.stopPropagation();
        toggle();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
    >
      {text}
    </div>
  );
};

const initialState: State = {
  openMerge: false,
  document1: '',
  document2: '',
  mergeDocumentAlert: '',
  creatingTransaction: false,
  pendingSendNotificationForExistingTransaction: undefined,
  ocrCreateOpen: false,
  orderDir: undefined,
  orderBy: 'vendor, timestamp',
  error: undefined,
  universalSearch: undefined,
  searching: false,
  fileData: undefined,
  imageWaiverTypePopupOpen: false,
  imageWaiverTypeFormData: {
    documentType: 'Receipt',
    invoiceWaiverType: 0,
  },
  transactionToSave: undefined,
  elementAction: 1,
};
export const TransactionTable: FC<Props> = ({
  isSelector,
  setTransactions,
  selectionLimit,
  selectorTitle,
  disabledParamSync,
  disabledSelectionIds,
  stickyHeaderSlot,
  ...rest
}) => {
  const auth = useAuth();
  const loggedUserId = auth.user.id;

  const queryClient = useQueryClient();

  const [state, dispatch] = useReducer(reducer, initialState);

  const [page, setPage] = useState<number>(0);

  // Export state
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'loaded'>('idle');
  const [exportData, setExportData] = useState<Array<Record<string, unknown>>>([]);
  const isExporting = exportStatus === 'loading';

  // Export page state
  const [exportPageStatus, setExportPageStatus] = useState<'idle' | 'loading' | 'loaded'>('idle');
  const [exportPageData, setExportPageData] = useState<Array<Record<string, unknown>>>([]);
  const isExportingPage = exportPageStatus === 'loading';

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState<ExportType>('all');
  const [pendingExportAction, setPendingExportAction] = useState<'page' | 'all' | null>(null);

  // QuickBooks Bills preview / sync (Accounts Payable admins)
  const [qboBillSyncOpen, setQboBillSyncOpen] = useState(false);

  const currentUserQuery = useUserQuery({
    filters: { id: loggedUserId },
    enabled: !!loggedUserId,
    select(data) {
      return {
        ...data,
        role: data.permissionGroups.find(
          (p) => p.type === 'role' && (p.name === 'Manager' || p.name === 'AccountsPayable'),
        )?.name,
        isAccountsPayableAdmin: !!data.permissionGroups.find(
          (p) => p.name === 'AccountsPayableAdmin',
        )?.name,
        isTransactionOCRUser: !!data.permissionGroups.find((p) => p.name === 'TransactionOCRUser')
          ?.name,
        isReceiptReviewer: !!data.permissionGroups.find(
          (p) => p.name === PERMISSION_NAME_RECEIPT_REVIEW,
        ),
      };
    },
  });

  const isUserAccountPayableAdmin = useMemo(
    () => auth.user.permissionGroups.some((p) => p.name === 'AccountsPayableAdmin'),
    [auth.user.permissionGroups],
  );
  const defaultStatusId = useMemo(() => {
    if (isSelector && 'defaultStatusId' in rest) {
      return rest.defaultStatusId;
    }
    if (isUserAccountPayableAdmin) {
      return 3;
    }

    return 2;
  }, [isSelector, isUserAccountPayableAdmin, rest]);

  const defaultJobId = useMemo(() => {
    if (isSelector && 'defaultJobId' in rest) {
      return rest.defaultJobId;
    }
    return undefined;
  }, [isSelector, rest]);

  const searchForm = useTransactionSearchForm({
    disableParamsSync: !!isSelector || disabledParamSync,
    defaultStatusId,
    defaultJobId,
  });

  const [transactionBatchGetParams, setTransactionBatchGetParams] =
    useState<TransactionQueryFilters>(() => searchForm.getValues());

  const transactionFilter = useMemo<TransactionQueryFilters>(() => {
    return {
      ...transactionBatchGetParams,
      pageNumber: page,
      isActive: 1,
      vendorCategory: "'PickTicket','Receipt','Invoice'",
      orderBy: state.orderBy ? state.orderBy : 'timestamp',
      orderDir: state.orderDir ? state.orderDir : state.orderDir == ' ' ? 'DESC' : 'DESC',
      includeTransactionLineItems: true,
    };
  }, [state.orderBy, state.orderDir, page, transactionBatchGetParams]);

  // Export filter (without pagination)
  const exportFilter = useMemo<TransactionQueryFilters>(() => {
    return {
      ...transactionBatchGetParams,
      isActive: 1,
      vendorCategory: "'PickTicket','Receipt','Invoice'",
      orderBy: state.orderBy ? state.orderBy : 'timestamp',
      orderDir: state.orderDir ? state.orderDir : state.orderDir == ' ' ? 'DESC' : 'DESC',
      includeTransactionLineItems: false, // Don't need line items for export
      withoutLimit: true, // Get all transactions, not just current page
    };
  }, [state.orderBy, state.orderDir, transactionBatchGetParams]);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [queryKeys.transaction.root, queryKeys.transaction.list],
    });
  }, [transactionFilter, queryClient]);

  const transactionsQuery = useTransactionBatchGetQuery({
    filters: transactionFilter,
    enabled: currentUserQuery.isSuccess && !!auth.user.id,
    staleTime: 60 * 1000, // 1 minute - prevents unnecessary refetches on tab focus
    refetchOnWindowFocus: false, // Disable refetch when user switches back to tab
  });

  // Export query for all transactions
  const exportAllTransactionsQuery = useTransactionBatchGetQuery({
    filters: exportFilter,
    enabled: false, // Only enabled when export is triggered
    select(data) {
      return data.results ?? [];
    },
  });

  // Every transaction matching the filters, the same set Export All downloads.
  const refetchAllTransactions = exportAllTransactionsQuery.refetch;
  const loadQBOBillTransactionIds = useCallback(async () => {
    const result = await refetchAllTransactions();
    if (result.error) throw result.error;
    return (result.data ?? []).map((transaction) => transaction.id);
  }, [refetchAllTransactions]);

  const [totalTransaction, setTotalTransactions] = useState<number>(0);

  useEffect(() => {
    if (transactionsQuery.isSuccess) {
      setTotalTransactions(transactionsQuery.data.totalCount ?? 0);
    }
  }, [transactionsQuery.data, transactionsQuery.isSuccess]);

  useEffect(() => {
    if (state.creatingTransaction) {
      createAccordionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [state.creatingTransaction]);

  const costCentersQuery = useTransactionAccountBatchGet({
    select(data) {
      const selectOptions =
        data?.results.map((account) => ({
          label: `${account.id}-${account.shortDescription}`,
          value: account.id,
        })) ?? [];
      selectOptions.push({ value: 0, label: 'None' });
      return {
        ...data,
        selectOptions,
      };
    },
  });
  const timesheetDepartmentQuery = useTimesheetDepartmentListQuery({
    filter: { isActive: 1, teamOnly: false, fieldMask: ['TeamOnly'] },
  });

  const createAccordionRef = useRef<HTMLDivElement>(null);

  // Open export dialog
  const handleOpenExportDialog = useCallback(
    (action: 'page' | 'all') => {
      if (action === 'page' && !transactionsQuery.data?.results.length) {
        toast({
          variant: 'destructive',
          title: 'No transaction data available to export',
        });
        return;
      }
      setPendingExportAction(action);
      setExportType('all'); // Reset to default
      setExportDialogOpen(true);
    },
    [transactionsQuery.data?.results],
  );

  // Confirm and execute export
  const handleConfirmExport = useCallback(
    async (selectedType: ExportType) => {
      setExportDialogOpen(false);
      setExportType(selectedType);

      const formatter: (transaction: Transaction) => Record<string, unknown> =
        selectedType === 'quickbooks' ? formatTransactionForQuickBooks : formatTransactionForExport;

      if (pendingExportAction === 'all') {
        // Export all transactions
        setExportStatus('loading');
        try {
          const dataForTheExport = (await exportAllTransactionsQuery.refetch()).data;
          if (!dataForTheExport) throw new Error('Failed to fetch transactions for the export');

          // Re-format with selected formatter
          const formattedData = dataForTheExport.map((transaction: Transaction) =>
            formatter(transaction),
          );
          setExportData(formattedData);
          setExportStatus('loaded');
        } catch (error) {
          console.error('Failed to fetch all transactions:', error);
          toast({
            variant: 'destructive',
            title: 'An error occurred while preparing the export. Please try again.',
          });
          setExportStatus('idle');
        }
      } else if (pendingExportAction === 'page') {
        // Export current page
        if (!transactionsQuery.data?.results.length) {
          toast({
            variant: 'destructive',
            title: 'No transaction data available to export',
          });
          return;
        }

        const pageData = transactionsQuery.data.results.map(formatter);
        setExportPageData(pageData);
        setExportPageStatus('loaded');
      }

      setPendingExportAction(null);
    },
    [pendingExportAction, exportAllTransactionsQuery, transactionsQuery.data?.results],
  );

  const handleExported = useCallback(() => {
    setExportStatus('idle');
  }, []);

  const handleExportPageExported = useCallback(() => {
    setExportPageStatus('idle');
  }, []);

  const handleNotifyUserOfExistingTransaction = useCallback(
    async (txn: Transaction) => {
      const foundDepartment = timesheetDepartmentQuery.data?.results.find(
        (department) => department.id == txn.departmentId,
      );
      if (foundDepartment) {
        const messageToSend = `A new transaction has been created in Accounts Payable that requires your attention, *Order Number:${txn.orderNumber}*, Amount: ${txn.amount} *Vendor: ${txn.vendor}*, *Notes: ${txn.notes}*`;
        console.log(messageToSend);
        const user = User.create();
        if (foundDepartment.id == 15) {
          const duane = 103896;
          user.id = duane; //Since ELE has no legit manager ID value that corresponds to a person, we are doing this for now.
        } else {
          user.id = foundDepartment.managerId;
        }
        if (user.id == 0) {
          console.log('failed to send message');
          dispatch({
            type: ACTIONS.SET_ERROR,
            data: 'Failed to Send Message, could not find user in Slack',
          });
        }
      }
      dispatch({
        type: ACTIONS.SET_PENDING_SEND_NOTIFICATION_FOR_EXISTING_TRANSACTION,
        data: undefined,
      });
    },
    [timesheetDepartmentQuery.data?.results],
  );

  const handleCloseAccordion = useCallback(() => {
    dispatch({
      type: ACTIONS.SET_CREATING_TRANSACTION,
      data: false,
    });
  }, []);
  useEffect(() => {
    const debouncedUpdate = debounce((data: TransactionSearchSchemeType) => {
      let dateRange: string[] | undefined = undefined;
      let dateTarget: string[] | undefined = undefined;
      if (data.dateFrom && data.dateTo) {
        const dateFrom = new Date(data.dateFrom);
        const dateTo = new Date(data.dateTo);
        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 999);
        dateRange = ['>=', timestamp(true, dateFrom), '<', timestamp(true, dateTo)];
        dateTarget = ['timestamp', 'timestamp'];
      }

      setTransactionBatchGetParams((prev) => {
        const newState: TransactionQueryFilters = {
          ...prev,
          ...data,
          searchPhrase: data.searchPhrase ? `%${data.searchPhrase}%` : undefined,
          amount: data.amount ? Number(data.amount) : undefined,
          jobId: data.jobId ? Number(data.jobId) : undefined,
          dateRange,
          dateTarget,
        };

        if (isEqual(prev, newState)) {
          return prev;
        }

        return newState;
      });
      setPage(0);
    }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME);

    const subscription = searchForm.watch(debouncedUpdate);

    return () => subscription.unsubscribe();
  }, [searchForm]);

  const handleFileLoad = useCallback((stateFile: string, file: string) => {
    if (stateFile == 'document1') {
      dispatch({
        type: ACTIONS.SET_MERGE_DOCUMENT1,
        data: file,
      });
    }
    if (stateFile == 'document2') {
      dispatch({
        type: ACTIONS.SET_MERGE_DOCUMENT2,
        data: file,
      });
    }
  }, []);
  const SCHEMA_MERGE_DOCUMENTS: Schema<MergeDocuments> = [
    [
      {
        name: 'document1',
        label: 'File',
        type: 'file',
        required: true,
        onFileLoad: (e) => handleFileLoad('document1', e as string),
      },
    ],
    [
      {
        name: 'document2',
        label: 'File2',
        type: 'file',
        required: true,
        onFileLoad: (e) => handleFileLoad('document2', e as string),
      },
    ],
  ];

  const transactionColumnDefs: ColumnDef<Transaction>[] = useMemo(() => {
    const selectorColumn: ColumnDef<Transaction> = {
      id: 'select',
      size: 44,
      header: ({ column }) => (
        <DataTableColumnHeader
          hideVisibilityToggle
          column={column}
          title={selectorTitle ? selectorTitle : 'Select exactly 2 transactions to merge'}
        />
      ),
      cell: ({ row }) =>
        disabledSelectionIds?.includes(row.original.id) ? (
          <div className="text-muted-foreground text-center text-[10px] font-medium leading-tight">
            Already included
          </div>
        ) : (
          <div className="flex size-6 items-center justify-center">
            <Checkbox
              onClick={(e) => e.stopPropagation()}
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label="Select row"
              className="mx-auto translate-y-0.5"
            />
          </div>
        ),
      enableSorting: false,
    };

    const columns: ColumnDef<Transaction>[] = [
      {
        enableSorting: true,
        accessorKey: 'timestamp',
        size: 72,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Date" />
        ),
        meta: {
          className: 'text-xs whitespace-nowrap',
        },
        cell: ({ row, column }) => {
          const date = new Date(parseISO(row.original.timestamp));

          return (
            <div className={column.columnDef.meta?.className}>
              {row.original.timestamp != NULL_TIME &&
              row.original.timestamp != '0000-00-00 00:00:00' &&
              isValid(date)
                ? format(date, 'yyyy-MM-dd')
                : '-'}
            </div>
          );
        },
      },
      {
        enableSorting: true,
        id: 'payment_due_date', // for BE sorting
        accessorKey: 'paymentDueDate',
        size: 72,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Due Date" />
        ),
        meta: {
          className: 'text-xs whitespace-nowrap',
        },
        cell: ({ row, column }) => {
          const paymentDueDate = row.original.paymentDueDate;

          if (
            !paymentDueDate ||
            paymentDueDate === NULL_TIME ||
            paymentDueDate === '0000-00-00 00:00:00'
          ) {
            return <div className={column.columnDef.meta?.className}>-</div>;
          }

          try {
            const date = new Date(paymentDueDate.split(' ').join('T'));
            return (
              <div className={column.columnDef.meta?.className}>
                {isValid(date) ? format(date, 'yyyy-MM-dd') : '-'}
              </div>
            );
          } catch {
            return <div className={column.columnDef.meta?.className}>-</div>;
          }
        },
      },
      {
        enableSorting: true,
        id: 'order_number', // for BE sorting
        accessorKey: 'orderNumber',
        size: 72,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="PO #" />
        ),
        cell: ({ row }) => <TruncatedCell text={row.original.orderNumber} />,
      },
      {
        enableSorting: true,
        id: 'invoice_number', // for BE sorting
        accessorKey: 'invoiceNumber',
        size: 76,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Invoice #" />
        ),
        cell: ({ row }) => <TruncatedCell text={row.original.invoiceNumber} />,
      },
      {
        enableSorting: true,
        id: 'owner_id', // for BE sorting
        accessorKey: 'ownerName',
        size: 90,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Purchaser/PM" />
        ),
        cell: ({ row }) => <TruncatedCell text={row.original.ownerName} />,
      },
      {
        enableSorting: true,
        id: 'assigned_employee_id', // for BE sorting
        accessorKey: 'assignedEmployeeName',
        size: 80,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Creator" />
        ),
        cell: ({ row }) => <TruncatedCell text={row.original.assignedEmployeeName} />,
      },
      {
        enableSorting: true,
        id: 'department_sort_field',
        size: 90,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Department" />
        ),
        cell: ({ row }) => <TruncatedCell text={row.original.department?.description} />,
      },
      {
        enableSorting: true,
        id: 'job_id', // for BE sorting
        accessorKey: 'jobId',
        size: 64,
        header: ({ column }) => (
          <DataTableColumnHeader
            hideVisibilityToggle
            column={column}
            className="mx-auto"
            title="Job #"
          />
        ),
        cell: ({ row }) => (
          <div>{row.original.jobId != 0 ? <JobPreview jobId={row.original.jobId} /> : 0}</div>
        ),
      },
      {
        accessorKey: 'cost_center_id',
        id: 'cost_center_id', // for BE sorting
        size: 80,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Cost Center" />
        ),
        cell: ({ row }) => <TruncatedCell text={row.original.costCenter?.description} />,
      },
      {
        accessorKey: 'amount',
        id: 'amount', // for BE sorting
        size: 72,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Amount" />
        ),
        cell: ({ row }) => (
          <div className="whitespace-nowrap">{prettyMoney(row.original.amount)}</div>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'vendor',
        id: 'vendor', // for BE sorting
        size: 84,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Vendor" />
        ),
        cell: ({ row }) => <TruncatedCell text={row.original.vendor} />,
        enableSorting: true,
      },
      {
        accessorKey: 'notes',
        id: 'notes', // for BE sorting
        size: 84,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Notes" />
        ),
        cell: ({ row }) => <ExpandableCell text={row.original.notes} />,
        enableSorting: true,
      },
      {
        accessorKey: 'statusId',
        id: 'status_id', // for BE sorting
        size: 68,
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle column={column} title="Status" />
        ),
        cell: ({ row }) => (
          <div>
            {row.original.statusId === 3 ? (
              <Tooltip key="accepted" content="Accepted">
                <IconButton size="small" key={'doneIcon'}>
                  <DoneIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <> </>
            )}
            {row.original.statusId == 4 ? (
              <div key="rectionReason">
                <Tooltip
                  key="rejected"
                  content={<TransactionRejectReason transactionId={row.original.id} />}
                >
                  <IconButton size="small" key="closeIcon">
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </div>
            ) : (
              <> </>
            )}
            {row.original.statusId === 5 ? (
              <Tooltip key="processed" content="Processed">
                <IconButton size="small" key="checkCircleIcon">
                  <CheckCircleOutlineIcon style={{ color: 'green' }} />
                </IconButton>
              </Tooltip>
            ) : (
              <> </>
            )}
          </div>
        ),
      },
      ...(isSelector
        ? []
        : [
            {
              id: 'actions',
              size: 128,
              header: ({ column }) => (
                <DataTableColumnHeader
                  hideVisibilityToggle
                  column={column}
                  className="text-center"
                  title="Actions"
                />
              ),
              cell: ({ row }) => (
                <div className="flex flex-wrap gap-1">
                  <TooltipProvider delayDuration={150}>
                    <TransactionActions
                      transaction={row.original}
                      page={page}
                      shouldDeleteAfterUpdate={transactionBatchGetParams.statusId !== 0}
                    />
                  </TooltipProvider>
                </div>
              ),
            } satisfies ColumnDef<Transaction>,
          ]),
    ];
    if (isSelector) {
      columns.unshift(selectorColumn);
    }
    return columns;
  }, [isSelector, page, transactionBatchGetParams.statusId, selectorTitle, disabledSelectionIds]);

  const columnDefs: ColumnDef<Transaction>[] = useMemo(() => {
    return transactionsQuery.isPending
      ? transactionColumnDefs.map((column) => ({
          ...column,
          cell: () => <Skeleton className="bg-foreground/30 h-4 w-20" />,
        }))
      : transactionColumnDefs;
  }, [transactionColumnDefs, transactionsQuery.isPending]);

  const [selectionState, setSelectionState] = useState<RowSelectionState>({});
  const onTableRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    (updater) => {
      if (transactionsQuery.isPending || transactionsQuery.isFetching || !setTransactions) {
        return;
      }

      const prevSelectionState = selectionState;
      const newSelectionState =
        typeof updater === 'function' ? updater(prevSelectionState) : updater;
      const newSelectionLength = Object.values(newSelectionState).filter(Boolean).length;
      const oldSelectionLength = Object.values(prevSelectionState).filter(Boolean).length;

      if (!newSelectionLength && !oldSelectionLength) {
        setTransactions([]);
        setSelectionState(newSelectionState);
        return;
      }

      // Strict enforcement of 2-transaction limit
      if (newSelectionLength > selectionLimit) {
        toast({
          variant: 'destructive',
          title: 'Selection limit reached',
          description: 'You can only select two transactions to merge at a time.',
        });
        return;
      }

      if (oldSelectionLength > newSelectionLength) {
        const removedTransactionId = Object.keys(prevSelectionState).find(
          (key) => !newSelectionState[key],
        );
        setTransactions((prev) => prev.filter((txn) => txn.id !== Number(removedTransactionId)));
        setSelectionState(newSelectionState);
        return;
      }

      if (newSelectionLength > oldSelectionLength) {
        const newTransactionId = Object.keys(newSelectionState).find(
          (key) => !prevSelectionState[key],
        );
        const newTransaction = transactionsQuery.data?.results.find(
          (tr) => tr.id === Number(newTransactionId),
        );

        if (newTransaction) {
          setTransactions((prev) => {
            if (prev.find((el) => el.id === newTransaction.id)) {
              return prev;
            }
            return [...prev, newTransaction];
          });
        }
        setSelectionState(newSelectionState);
        return;
      }

      if (newSelectionLength === oldSelectionLength) {
        setTransactions((prev) => prev.filter((txn) => !!newSelectionState[txn.id]));
        setSelectionState(newSelectionState);
        return;
      }
    },
    [
      selectionState,
      setTransactions,
      transactionsQuery.data?.results,
      selectionLimit,
      transactionsQuery.isFetching,
      transactionsQuery.isPending,
    ],
  );

  const selectAllPage = useCallback(() => {
    if (!setTransactions) throw new Error('selectAllPage is called in non selector mode');
    if (!transactionsQuery.isSuccess || transactionsQuery.data.results.length < 1) {
      toast({
        variant: 'destructive',
        title: 'No transactions to select',
      });
      return;
    }

    // Get current selection count
    const currentSelectionCount = Object.values(selectionState).filter(Boolean).length;
    const availableSlots = selectionLimit - currentSelectionCount;

    if (availableSlots <= 0) {
      toast({
        variant: 'destructive',
        title: 'Selection limit reached',
        description: `You can only select ${selectionLimit} transactions at a time.`,
      });
      return;
    }

    // Only select up to the available slots
    const transactionsToSelect = transactionsQuery.data.results
      .filter((transaction) => !disabledSelectionIds?.includes(transaction.id))
      .slice(0, availableSlots);

    setSelectionState((prev) => {
      transactionsToSelect.forEach((transaction) => {
        prev[transaction.id] = true;
      });
      return prev;
    });

    setTransactions((prev) => {
      const currentPageTransactionIds = transactionsToSelect.map((transaction) => transaction.id);
      return prev
        .filter((transaction) => !currentPageTransactionIds.includes(transaction.id))
        .concat(transactionsToSelect);
    });
  }, [
    setTransactions,
    transactionsQuery.data?.results,
    transactionsQuery.isSuccess,
    selectionLimit,
    selectionState,
    disabledSelectionIds,
  ]);

  const tableData = useMemo(() => {
    return transactionsQuery.isPending
      ? Array.from({ length: 5 }, (_, idx) => Transaction.create({ id: idx }))
      : transactionsQuery.data?.results ?? [];
  }, [transactionsQuery.data?.results, transactionsQuery.isPending]);

  const [tableSortingState, setTableSortingState] = useState<SortingState>([]);

  const onTableSortChange: OnChangeFn<SortingState> = useCallback((updater) => {
    setTableSortingState((prev) => {
      const newState = typeof updater === 'function' ? updater(prev) : updater;
      dispatch({
        type: ACTIONS.SET_ORDER,
        data: {
          orderBy: newState[0].id,
          orderDir: newState[0].desc ? 'DESC' : 'ASC',
        },
      });
      setPage(0);
      return newState;
    });
  }, []);

  const table = useReactTable({
    data: tableData,
    columns: columnDefs,
    state: {
      pagination: {
        pageIndex: page,
        pageSize: 50,
      },
      rowSelection: selectionState,
      sorting: tableSortingState,
    },
    getRowId: (transaction) => transaction.id.toString(),
    enableRowSelection: (row) => !!isSelector && !disabledSelectionIds?.includes(row.original.id),
    onRowSelectionChange: onTableRowSelectionChange,
    onSortingChange: onTableSortChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const imageDimensionToFit = (image: PDFImage, container: { width: number; height: number }) => {
    if (
      Math.min(image.width, container.width) === image.width &&
      Math.min(image.height, container.height) === image.height
    )
      return { width: image.width, height: image.height };
    const image_ratio = image.width / image.height;
    const container_ratio = container.width / container.height;
    if (container_ratio > image_ratio) {
      return {
        width: (image.width * container.height) / image.height,
        height: container.height,
      };
    } else {
      return {
        width: container.width,
        height: (image.height * container.width) / image.width,
      };
    }
  };

  const onRowClick = useMemo(() => {
    return isSelector
      ? (t: Transaction) => {
          if (disabledSelectionIds?.includes(t.id)) {
            return;
          }
          onTableRowSelectionChange((prev) => {
            if (prev[t.id.toString()]) {
              delete prev[t.id.toString()];
              return { ...prev };
            } else {
              return { ...prev, [t.id.toString()]: true };
            }
          });
        }
      : undefined;
  }, [isSelector, onTableRowSelectionChange, disabledSelectionIds]);

  const [isMergingModalOpen, setIsMergingModalOpen] = useState(false);
  const closeMergeModal = useCallback(() => {
    setIsMergingModalOpen(false);
  }, []);

  const resetTransactions = useCallback(async () => {
    try {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.transaction.root],
      });
      setTransactionBatchGetParams((prev) => ({
        ...prev,
        page: 0,
        departmentId: 0,
        jobId: undefined,
        searchPhrase: '',
        assignedEmployeeId: 0,
        ownerId: 0,
        statusId: 0,
        orderBy: 'timestamp',
        orderDir: 'DESC',
      }));
      searchForm.reset(searchForm.formState.defaultValues);
      setTransactions?.([]);
      setSelectionState({});
    } catch (err) {
      console.error(`An error occurred while invalidation of transaction data: ${err}`);
    }
    closeMergeModal();
  }, [queryClient, setTransactions, closeMergeModal, searchForm]);

  const uploadTransactionWithPhotoRef = useRef<HTMLDivElement>(null);
  const [isUploadTransactionWithPhotoOpen, setIsUploadTransactionWithPhotoOpen] = useState(false);
  const closeUploadTransactionWithPhoto = useCallback(() => {
    setIsUploadTransactionWithPhotoOpen(false);
  }, []);
  const openUploadTransactionWithPhoto = useCallback(() => {
    setIsUploadTransactionWithPhotoOpen(true);
    uploadTransactionWithPhotoRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, []);

  const toPdfPromise = async (file: string) => {
    const pdf = await PDFDocument.create();

    const page = pdf.addPage(PageSizes.A4);
    const imageUInt8Array = file;

    try {
      const tempImage = await pdf.embedJpg(imageUInt8Array);
      const [a4_width, a4_height] = PageSizes.A4;
      const dimensions = imageDimensionToFit(tempImage, {
        width: a4_width,
        height: a4_height,
      });
      page.drawImage(tempImage, {
        x: (a4_width - dimensions.width) / 2,
        y: (a4_height - dimensions.height) / 2,
        width: dimensions.width,
        height: dimensions.height,
      });
      return { pdf: pdf.save(), error: '' };
    } catch {
      console.log('failed to embed jpeg, trying png');
      try {
        const tempImage = await pdf.embedPng(imageUInt8Array);
        const [a4_width, a4_height] = PageSizes.A4;
        const dimensions = imageDimensionToFit(tempImage, {
          width: a4_width,
          height: a4_height,
        });
        page.drawImage(tempImage, {
          x: (a4_width - dimensions.width) / 2,
          y: (a4_height - dimensions.height) / 2,
          width: dimensions.width,
          height: dimensions.height,
        });
        return { pdf: pdf.save(), error: '' };
      } catch (e) {
        console.log('something went wrong with drawing the image', e);
        return { pdf: pdf.save(), error: 'Could not Draw Image on PDF' };
      }
    }
  };

  const mergePdf = async () => {
    const mergedPdf = await PDFDocument.create();
    let pdfA = await PDFDocument.create();
    let pdfB = await PDFDocument.create();
    let error = '';
    try {
      pdfA = await PDFDocument.load(state.document1);
    } catch {
      try {
        const resultA = await toPdfPromise(state.document1);
        pdfA = await PDFDocument.load(await resultA.pdf);
        if (resultA.error != '') {
          error = resultA.error;
        }
        console.log('first document not a pdf');
      } catch (e) {
        console.log('failed to load document a as pdf or image ', e);
        dispatch({
          type: ACTIONS.SET_MERGE_DOCUMENT_ALERT,
          data: 'Could not generate PDF',
        });
      }
    }
    try {
      pdfB = await PDFDocument.load(state.document2);
    } catch (e) {
      try {
        const resultB = await toPdfPromise(state.document2);
        pdfB = await PDFDocument.load(await resultB.pdf);
        if (resultB.error != '') {
          error = resultB.error;
        }
      } catch (e) {
        console.log('failed to load document b as a pdf and image ', e);
        dispatch({
          type: ACTIONS.SET_MERGE_DOCUMENT_ALERT,
          data: 'Could not generate PDF',
        });
      }

      console.log('second document not a pdf ', e);
    }
    console.log('loading documents');
    if (error != '') {
      dispatch({
        type: ACTIONS.SET_MERGE_DOCUMENT_ALERT,
        data: 'Could not generate PDF',
      });
    } else {
      const copiedPagesA = await mergedPdf.copyPages(pdfA, pdfA.getPageIndices());

      copiedPagesA.forEach((page) => mergedPdf.addPage(page));

      const copiedPagesB = await mergedPdf.copyPages(pdfB, pdfB.getPageIndices());

      copiedPagesB.forEach((page) => mergedPdf.addPage(page));

      const mergedPdfFile = await mergedPdf.save();

      const link = document.createElement('a');
      const blob = new Blob([new Uint8Array(mergedPdfFile)], { type: 'application/pdf' });
      link.href = window.URL.createObjectURL(blob);

      const fileName = 'MergedDocument';
      link.download = fileName;
      link.click();
      dispatch({
        type: ACTIONS.SET_OPEN_MERGE,
        data: false,
      });
    }
    dispatch({ type: ACTIONS.SET_MERGE_DOCUMENT1, data: '' });
    dispatch({ type: ACTIONS.SET_MERGE_DOCUMENT2, data: '' });
  };

  return (
    <ErrorBoundary key="ErrorBoundary">
      {state.openMerge && (
        <Modal
          open={state.openMerge}
          onClose={() => {
            dispatch({ type: ACTIONS.SET_MERGE_DOCUMENT1, data: '' });
            dispatch({ type: ACTIONS.SET_MERGE_DOCUMENT2, data: '' });
            dispatch({ type: ACTIONS.SET_OPEN_MERGE, data: false });
          }}
        >
          <Alert
            open={state.mergeDocumentAlert != ''}
            onClose={() => dispatch({ type: ACTIONS.SET_MERGE_DOCUMENT_ALERT, data: '' })}
            label="Okay"
          >
            There was an Error generating your PDF. There may be something wrong with the file. If
            you continue to notice this error, please contact webtech
          </Alert>
          <Form<MergeDocuments>
            key="mergeDocuments"
            title="Merge PDFs"
            schema={SCHEMA_MERGE_DOCUMENTS}
            data={{ document1: state.document1, document2: state.document2 }}
            onSave={mergePdf}
            submitLabel="Save"
            onClose={() => {
              dispatch({ type: ACTIONS.SET_MERGE_DOCUMENT1, data: '' });
              dispatch({ type: ACTIONS.SET_MERGE_DOCUMENT2, data: '' });
              dispatch({ type: ACTIONS.SET_OPEN_MERGE, data: false });
            }}
          ></Form>
        </Modal>
      )}
      {state.ocrCreateOpen && (
        <Modal
          open={state.ocrCreateOpen}
          onClose={() => {
            dispatch({ type: ACTIONS.SET_OCR_CREATE_OPEN, data: false });
          }}
        >
          <UploadPhotoOCR
            onClose={() => dispatch({ type: ACTIONS.SET_OCR_CREATE_OPEN, data: false })}
            accountsPayableWorkflow={true}
          />
        </Modal>
      )}
      {state.error && (
        <Alert
          open={state.error != undefined}
          onClose={() => dispatch({ type: ACTIONS.SET_ERROR, data: undefined })}
          title="Error"
        >
          {state.error}
        </Alert>
      )}
      <div className="lg:bg-background lg:sticky lg:top-0 lg:z-50">
        {stickyHeaderSlot}
        <FormContext {...searchForm}>
          <TransactionSearchForm
            disableStatusPicker={
              'disableFilterStatusSelector' in rest ? rest.disableFilterStatusSelector : false
            }
          />
        </FormContext>

        <div className="flex flex-col items-center justify-between gap-2 p-4 md:flex-row">
          <div className="flex flex-col items-center gap-2 sm:flex-row">
            <h1 className="text-xl font-semibold tracking-wide">Transactions</h1>
            <DataTablePagination
              currentPage={page}
              setPage={setPage}
              pageCount={Math.ceil(totalTransaction / 50)}
            />
            {!!totalTransaction && <p>Total Entries: {totalTransaction}</p>}
            {isSelector && !isFinite(selectionLimit) && (
              <Button
                disabled={!transactionsQuery.isSuccess || transactionsQuery.data.results.length < 1}
                onClick={selectAllPage}
              >
                Select All
              </Button>
            )}
          </div>

          {currentUserQuery.isSuccess && !isSelector && (
            <div>
              {currentUserQuery.data?.isAccountsPayableAdmin ||
              currentUserQuery.data?.isReceiptReviewer ||
              currentUserQuery.data?.role == 'Manager' ? (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    onClick={() =>
                      dispatch({
                        type: ACTIONS.SET_CREATING_TRANSACTION,
                        data: !state.creatingTransaction,
                      })
                    }
                  >
                    New Transaction
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        {isExporting || isExportingPage ? <LoadingIcon className="mr-2" /> : null}⋯
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          dispatch({
                            type: ACTIONS.SET_OPEN_MERGE,
                            data: true,
                          })
                        }
                      >
                        Merge 2 Documents
                      </DropdownMenuItem>

                      <DropdownMenuItem onClick={() => setIsMergingModalOpen(true)}>
                        Merge Transactions
                      </DropdownMenuItem>

                      {currentUserQuery.data?.isTransactionOCRUser && (
                        <DropdownMenuItem
                          onClick={() =>
                            dispatch({
                              type: ACTIONS.SET_OCR_CREATE_OPEN,
                              data: !state.ocrCreateOpen,
                            })
                          }
                        >
                          Create Transaction OCR
                        </DropdownMenuItem>
                      )}

                      <DropdownMenuItem
                        onClick={() => handleOpenExportDialog('page')}
                        disabled={!transactionsQuery.data?.results.length || isExportingPage}
                      >
                        {isExportingPage ? <LoadingIcon className="mr-2" /> : null}
                        Export Page
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => handleOpenExportDialog('all')}
                        disabled={isExporting}
                      >
                        {isExporting ? <LoadingIcon className="mr-2" /> : null}
                        Export All
                      </DropdownMenuItem>

                      {currentUserQuery.data?.isAccountsPayableAdmin && (
                        <DropdownMenuItem onClick={() => setQboBillSyncOpen(true)}>
                          Preview QuickBooks Bills
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button onClick={openUploadTransactionWithPhoto}>
                    Upload Pick Ticket, Invoice, or Non Credit Card Receipt
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        {isExporting || isExportingPage ? <LoadingIcon className="mr-2" /> : null}⋯
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleOpenExportDialog('page')}
                        disabled={!transactionsQuery.data?.results.length}
                      >
                        Export Page
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={() => handleOpenExportDialog('all')}
                        disabled={isExporting}
                      >
                        {isExporting ? <LoadingIcon /> : 'Export All'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          )}

          <Dialog open={isMergingModalOpen} onOpenChange={setIsMergingModalOpen}>
            <DialogContent className="flex h-dvh min-w-full flex-col p-0">
              <CompareTransactions onMerge={resetTransactions} selectionLimit={2} />
            </DialogContent>
          </Dialog>

          {qboBillSyncOpen && (
            <QuickBooksBillSyncDialog
              onClose={() => setQboBillSyncOpen(false)}
              loadTransactionIds={loadQBOBillTransactionIds}
              adminId={loggedUserId}
            />
          )}

          {/* Export Type Selection Dialog */}
          <TransactionExportDialog
            isOpen={exportDialogOpen}
            onClose={() => setExportDialogOpen(false)}
            onConfirm={handleConfirmExport}
          />
        </div>
      </div>

      {/* Hidden ExportJSON components for file downloads */}
      {!isSelector && (
        <TransactionExportComponents
          exportPageData={exportPageData}
          exportData={exportData}
          exportType={exportType}
          exportPageStatus={exportPageStatus}
          exportStatus={exportStatus}
          onExportPageExported={handleExportPageExported}
          onExported={handleExported}
        />
      )}
      {!isSelector && (
        <div ref={createAccordionRef}>
          <TransactionCreateAccordion
            isOpen={state.creatingTransaction}
            onClose={handleCloseAccordion}
            onSave={handleCloseAccordion}
          />
        </div>
      )}

      {!isSelector && (
        <div ref={uploadTransactionWithPhotoRef}>
          <Accordion collapsible value={String(isUploadTransactionWithPhotoOpen)} type="single">
            <AccordionItem value="true">
              <AccordionContent>
                <UploadPhotoTransaction
                  loggedUserId={loggedUserId!}
                  costCenters={TransactionAccountList.create({
                    results: costCentersQuery.data?.results ?? [],
                    totalCount: costCentersQuery.data?.totalCount ?? 0,
                  })}
                  onClose={closeUploadTransactionWithPhoto}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}

      <div className="max-w-[100vw] flex-1 overflow-auto [&_th]:px-2 [&_th_button]:-ml-2 [&_th_button]:h-auto [&_th_button]:min-h-8 [&_th_button]:whitespace-normal [&_th_button]:px-2 [&_th_button]:py-1 [&_th_button]:text-left [&_th_button_svg]:ml-1 [&_th_button_svg]:shrink-0">
        <DataTable
          enableColumnSizing
          onRowClick={onRowClick}
          shouldApplyOnRowClick={
            disabledSelectionIds?.length
              ? (transaction) => !disabledSelectionIds.includes(transaction.id)
              : undefined
          }
          rowClassName={
            disabledSelectionIds?.length
              ? (row) => (disabledSelectionIds.includes(row.original.id) ? 'opacity-50' : undefined)
              : undefined
          }
          RowWrapper={RowTransactionLineItemContextWrapper}
          table={table}
          tableCellClassName="md:px-2 md:py-2"
        />
      </div>

      {state.pendingSendNotificationForExistingTransaction != undefined && (
        <Confirm
          open={state.pendingSendNotificationForExistingTransaction != undefined}
          submitLabel="Send"
          title="Notify Manager"
          onConfirm={() =>
            handleNotifyUserOfExistingTransaction(
              state.pendingSendNotificationForExistingTransaction!,
            )
          }
          onClose={() =>
            dispatch({
              type: ACTIONS.SET_PENDING_SEND_NOTIFICATION_FOR_EXISTING_TRANSACTION,
              data: undefined,
            })
          }
        >
          Notify Manager of Transaction?
        </Confirm>
      )}
    </ErrorBoundary>
  );
};
